import { createApp } from 'vue'
import { createPinia } from 'pinia'
import Toast from 'vue-toastification'
import 'vue-toastification/dist/index.css'
import router from './router'
import App from './App.vue'
import 'bootstrap/dist/css/bootstrap.min.css'
import 'bootstrap-icons/font/bootstrap-icons.css'
import 'bootstrap/dist/js/bootstrap.bundle.min.js'
import './assets/css/premium.css'
import Echo from 'laravel-echo'
import Pusher from 'pusher-js'

// laravel-echo's 'reverb' broadcaster is implemented as a wrapper around the
// Pusher connector (Reverb speaks the Pusher protocol), which requires a
// Pusher client to be globally available before `new Echo(...)` runs -
// without this, Echo throws "Pusher client not found" on load. Same
// placement as public_admin's working Reverb setup (useWebSocket.js).
window.Pusher = Pusher

const app = createApp(App)
const pinia = createPinia()

app.use(pinia)
app.use(router)
app.use(Toast, {
  position: 'top-right',
  timeout: 4000,
  closeOnClick: true,
  pauseOnFocusLoss: true,
  pauseOnHover: true,
  draggable: true,
  draggablePercent: 0.6,
  showCloseButtonOnHover: false,
  hideProgressBar: false,
  closeButton: 'button',
  icon: true,
  rtl: false,
  transition: 'Vue-Toastification__bounce',
  maxToasts: 5,
  newestOnTop: true,
})

// Initialize Reverb WebSocket connection
const reverbKey = import.meta.env.VITE_REVERB_APP_KEY || '6f78fde54fdafa66b987a1b48a03508b40bf4aa6'
const reverbHost = import.meta.env.VITE_REVERB_HOST || 'localhost'
const reverbPort = import.meta.env.VITE_REVERB_PORT || 8080
const reverbScheme = import.meta.env.VITE_REVERB_SCHEME || 'http'

// Private/presence channels (e.g. the 'app' presence channel App.vue joins)
// need a bearer token to authorize via /broadcasting/auth - same token key
// and endpoint api.core.js already uses for every other authenticated
// request. Without this the join is always rejected (channel authorization
// runs before any .here()/.joining() callback ever fires).
//
// A plain `auth.headers.Authorization: Bearer ${token}` here would read
// va_token exactly once, at this module's load time - fine for a page
// reload while already logged in, but wrong for the very common case of
// loading the app logged out (or on the login page) and logging in via
// client-side navigation afterward: Echo is a module-level singleton built
// once, so that static header would stay empty/stale for the rest of the
// session, 401ing every channel join from then on even after a real login
// succeeded. A custom authorizer re-reads localStorage at the moment each
// channel is actually authorized instead of once at startup.
const authEndpoint = `${import.meta.env.VITE_API_BASE_URL || '/api/v1'}/broadcasting/auth`

window.Echo = new Echo({
  broadcaster: 'reverb',
  key: reverbKey,
  wsHost: reverbHost,
  wsPort: reverbPort,
  wssPort: 443,
  forceTLS: reverbScheme === 'https',
  enabledTransports: ['ws', 'wss'],
  authorizer: (channel) => ({
    authorize: (socketId, callback) => {
      fetch(authEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${localStorage.getItem('va_token') || ''}`,
        },
        body: JSON.stringify({ socket_id: socketId, channel_name: channel.name }),
      })
        .then((res) => (res.ok ? res.json() : Promise.reject(res)))
        .then((data) => callback(false, data))
        .catch((err) => callback(true, err))
    },
  }),
})

console.log('[Reverb] WebSocket connection initialized on ' + reverbHost + ':' + reverbPort)

app.mount('#app')
